import { CANVAS_HEIGHT, FLOOR_HEIGHT, BALL_RADIUS } from '../constants';
import { clamp, convertHeightToDisplayUnits, lerp } from '../utils';
import ViewportManager from '../viewportManager';

const TICK_COUNT = 7;
const LERP_SPEED = 0.15;

export default class HeightRulerUI {
    private container: HTMLDivElement;
    private ticksContainer: HTMLDivElement | null = null;
    private indicatorElement: HTMLDivElement | null = null;
    private tickElements: HTMLDivElement[] = [];
    private valueElement: HTMLSpanElement | null = null;
    private baselineHeight: number = 0;
    private currentHeight: number = 0;
    private targetHeight: number = 0;
    private animationFrame: number | null = null;
    private viewportManager: ViewportManager | null;
    private maxHeight: number = 0;

    constructor(viewportManager?: ViewportManager) {
        this.viewportManager = viewportManager || null;
        this.maxHeight = CANVAS_HEIGHT - FLOOR_HEIGHT;
        this.container = document.createElement('div');
        this.container.className = 'golfup-height-ruler';
        this.render();
    }

    /**
     * Re-render the height ruler with updated max height (e.g., when canvas height changes in snakeup mode)
     * This should be called after game mode initialization if canvas height changed
     */
    public updateMaxHeight(): void {
        const newMaxHeight = CANVAS_HEIGHT - FLOOR_HEIGHT;
        if (newMaxHeight !== this.maxHeight) {
            this.maxHeight = newMaxHeight;
            // Re-render to update tick marks
            this.render();
        }
    }

    private render(): void {
        // Generate static ticks from max height to 0 (reversed - max at top, 0 at bottom)
        const maxHeightMeters = Math.round(convertHeightToDisplayUnits(this.maxHeight));
        const tickValues: number[] = [];
        for (let i = 0; i < TICK_COUNT; i++) {
            const tickValue = Math.round((maxHeightMeters / (TICK_COUNT - 1)) * i);
            tickValues.push(tickValue);
        }
        // Reverse the array so max height is at top, 0m is at bottom
        tickValues.reverse();
        const tickMarkup = tickValues.map(value => `<div>${value}m</div>`).join('');

        this.container.innerHTML = `
            <div class="golfup-height-ruler__spine">
                <div class="golfup-height-ruler__ticks">
                    ${tickMarkup}
                </div>
                <div class="golfup-height-ruler__indicator">
                    <div class="golfup-height-ruler__tag">
                        <span class="golfup-height-ruler__value">000m</span>
                    </div>
                </div>
            </div>
        `;

        this.ticksContainer = this.container.querySelector('.golfup-height-ruler__ticks');
        this.indicatorElement = this.container.querySelector('.golfup-height-ruler__indicator');
        this.tickElements = this.ticksContainer ? Array.from(this.ticksContainer.children) as HTMLDivElement[] : [];
        this.valueElement = this.container.querySelector('.golfup-height-ruler__value');

        document.body.appendChild(this.container);
    }

    public setBaseline(baselineHeight: number): void {
        this.baselineHeight = Math.max(0, baselineHeight);
        this.currentHeight = 0;
        this.targetHeight = 0;
        this.updateUI();
    }

    public setHeight(height: number): void {
        const adjustedHeight = Math.max(0, height - this.baselineHeight);
        this.targetHeight = adjustedHeight;
        if (this.animationFrame === null) {
            this.animate();
        }
    }

    private animate(): void {
        this.currentHeight = lerp(this.currentHeight, this.targetHeight, LERP_SPEED);

        if (Math.abs(this.currentHeight - this.targetHeight) < 0.5) {
            this.currentHeight = this.targetHeight;
            this.animationFrame = null;
        } else {
            this.animationFrame = requestAnimationFrame(() => this.animate());
        }

        this.updateUI();
    }

    private updateUI(): void {
        if (!this.valueElement || !this.indicatorElement) {
            return;
        }

        // Recalculate max height in case CANVAS_HEIGHT changed
        const maxHeight = CANVAS_HEIGHT - FLOOR_HEIGHT;

        const displayHeight = convertHeightToDisplayUnits(this.currentHeight);
        this.valueElement.textContent = `${Math.round(displayHeight)}m`;

        // Update indicator position based on height percentage
        // Height 0 = bottom, max height = top
        const heightRatio = maxHeight > 0 ? clamp(this.currentHeight / maxHeight, 0, 1) : 0;

        // Get the ruler container bounds
        const rulerRect = this.container.getBoundingClientRect();
        const rulerTop = rulerRect.top;
        const rulerBottom = rulerRect.bottom;

        // Calculate available space for indicator (same as ticks container: top: 4rem, bottom: 4rem)
        const tickContainerTop = rulerTop + (4 * 16); // 4rem in pixels
        const tickContainerBottom = rulerBottom - (4 * 16); // 4rem in pixels
        const tickContainerHeight = tickContainerBottom - tickContainerTop;

        // Position indicator: 0% height = bottom, 100% height = top
        // Invert the ratio so 0 height is at bottom (1 - heightRatio)
        const indicatorTop = tickContainerBottom - (tickContainerHeight * heightRatio);

        // Convert to percentage from top of ruler
        const indicatorTopPercent = ((indicatorTop - rulerTop) / (rulerBottom - rulerTop)) * 100;

        // Set position using top percentage
        this.indicatorElement.style.top = `${indicatorTopPercent}%`;
        this.indicatorElement.style.transform = 'translateY(-50%) rotate(-2deg)';
    }

    public syncToCamera(): void {
        if (!this.viewportManager) return;
        const visibleWorldHeight = this.getVisibleWorldHeight();
        const cameraCenterWorldY = this.viewportManager.scrollPosition + visibleWorldHeight / 2;
        const cameraHeight = this.worldYToHeight(cameraCenterWorldY);
        this.setHeight(cameraHeight);
    }

    private worldYToHeight(worldY: number): number {
        // Bottom of playable area is at CANVAS_HEIGHT - FLOOR_HEIGHT
        const zeroWorldY = CANVAS_HEIGHT - FLOOR_HEIGHT;
        return Math.max(0, zeroWorldY - worldY);
    }

    private getVisibleWorldHeight(): number {
        if (this.viewportManager && typeof this.viewportManager.getVisibleHeight === 'function') {
            return this.viewportManager.getVisibleHeight();
        }
        return window.innerHeight;
    }

    public destroy(): void {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
        this.tickElements = [];
        this.valueElement = null;
        this.ticksContainer = null;
        this.indicatorElement = null;
    }
}

